import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { motion } from "framer-motion";
import PageHeader from "./PageHeader";

const UIExtensionPage = () => {
  useEffect(() => {
    // Update document title and meta description for SEO
    document.title = "ChatGPT UI Change Extension | Power-Up Chat";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Want to customize ChatGPT\'s interface? Power-Up Chat adds folders, templates, and bulk actions to your ChatGPT screen.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader />
      
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Change ChatGPT's UI with One Extension
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Transform your ChatGPT experience without any coding or complex setup. Power-Up Chat adds the features you need directly to your ChatGPT interface.
          </p>
        </motion.div>

        {/* Main Question */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-xl p-8 mb-12 shadow-lg"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Can I Change ChatGPT's UI?</h2>
          <p className="text-gray-700 mb-6">
            Yes! While ChatGPT's core interface is controlled by OpenAI, browser extensions like ChatGPT Power-Up can enhance and customize your experience by adding new features and functionality directly to the page.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 p-6 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-3">✅ What Extensions Can Do</h3>
              <ul className="text-blue-700 space-y-2">
                <li>• Add new UI elements and buttons</li>
                <li>• Enhance existing functionality</li>
                <li>• Add folders and organization tools</li>
                <li>• Include prompt templates and shortcuts</li>
                <li>• Improve navigation and search</li>
              </ul>
            </div>
            <div className="bg-orange-50 p-6 rounded-lg">
              <h3 className="font-semibold text-orange-800 mb-3">⚠️ What Extensions Cannot Do</h3>
              <ul className="text-orange-700 space-y-2">
                <li>• Change ChatGPT's core AI behavior</li>
                <li>• Modify OpenAI's servers or models</li>
                <li>• Access your private data or conversations</li>
                <li>• Bypass ChatGPT's terms of service</li>
                <li>• Replace ChatGPT's fundamental interface</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* What's Different */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">What's Different with Power-Up Installed</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-semibold text-gray-900 mb-4">📁 Enhanced Sidebar</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">Folder organization system</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">Drag & drop chat management</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">Bulk selection and actions</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">Improved search functionality</span>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-semibold text-gray-900 mb-4">⚡ Prompt Tools</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">Saved prompt templates</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">One-click prompt insertion</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">Keyboard shortcuts</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-700">Category organization</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Non-Invasive Design */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-8 mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Non-Invasive Design Philosophy</h2>
          <p className="text-gray-700 mb-6">
            Power-Up Chat is designed to enhance ChatGPT, not replace it. We maintain the familiar interface you know and love while adding the features power users need.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Familiar Layout</h3>
              <p className="text-gray-600 text-sm">ChatGPT looks and feels the same, just with enhanced functionality.</p>
            </div>
            <div className="text-center">
              <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                <span className="text-2xl">🔒</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">No Account Required</h3>
              <p className="text-gray-600 text-sm">Works instantly after installation. No sign-ups or subscriptions needed.</p>
            </div>
            <div className="text-center">
              <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">UI Stays Fast</h3>
              <p className="text-gray-600 text-sm">Optimized for performance. No impact on ChatGPT's speed or responsiveness.</p>
            </div>
          </div>
        </motion.div>

        {/* Technical Details */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="bg-white rounded-xl p-8 mb-12 shadow-lg"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Technical Details & Security</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">🔧 How It Works</h3>
              <p className="text-gray-700 mb-4">Power-Up Chat is a Chrome extension that runs locally in your browser. It enhances ChatGPT's interface by adding new UI elements and functionality.</p>
              <ul className="space-y-2 text-gray-600">
                <li>• Runs entirely in your browser</li>
                <li>• No external servers or data collection</li>
                <li>• Works with ChatGPT's existing API</li>
                <li>• Updates automatically with ChatGPT</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">🛡️ Security & Privacy</h3>
              <p className="text-gray-700 mb-4">Your data stays private and secure. The extension only accesses ChatGPT's interface to add features.</p>
              <ul className="space-y-2 text-gray-600">
                <li>• No access to your conversations</li>
                <li>• No data sent to external servers</li>
                <li>• Open source and transparent</li>
                <li>• Compliant with ChatGPT's terms</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="bg-white rounded-xl p-8 mb-12 shadow-lg"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Is it allowed to use ChatGPT UI extensions?</h3>
              <p className="text-gray-600">Yes! Browser extensions that enhance ChatGPT's interface are allowed as long as they don't violate OpenAI's terms of service. Power-Up Chat only adds helpful features without interfering with ChatGPT's core functionality.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Do I need to know how to code?</h3>
              <p className="text-gray-600">Absolutely not! Power-Up Chat is designed for everyone. Simply install the extension and start using the new features immediately. No technical knowledge required.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Will it slow down ChatGPT?</h3>
              <p className="text-gray-600">No, Power-Up Chat is optimized for performance. You won't notice any difference in ChatGPT's speed or responsiveness. The extension runs efficiently in the background.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Can I uninstall it if I don't like it?</h3>
              <p className="text-gray-600">Yes, you can uninstall Power-Up Chat at any time through Chrome's extension manager. Your ChatGPT will return to its original state immediately.</p>
            </div>
          </div>
        </motion.div>

        {/* Related Features */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="bg-white rounded-xl p-8 mb-12 shadow-lg"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Explore All Power-Up Features</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/chatgpt-folders" className="block">
              <div className="border border-gray-200 rounded-lg p-6 hover:border-blue-300 hover:shadow-md transition-all">
                <h3 className="font-semibold text-gray-900 mb-2">ChatGPT Folders</h3>
                <p className="text-gray-600 text-sm">Organize your conversations into folders for better workflow management.</p>
              </div>
            </Link>
            <Link to="/chatgpt-prompt-templates" className="block">
              <div className="border border-gray-200 rounded-lg p-6 hover:border-blue-300 hover:shadow-md transition-all">
                <h3 className="font-semibold text-gray-900 mb-2">ChatGPT Prompt Templates</h3>
                <p className="text-gray-600 text-sm">Save and reuse your best prompts with our powerful template system.</p>
              </div>
            </Link>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.4 }}
          className="text-center bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-8 text-white"
        >
          <h2 className="text-3xl font-bold mb-4">Install Free – Make ChatGPT Work For You</h2>
          <p className="text-xl mb-8 opacity-90">
            Transform your ChatGPT experience with powerful UI enhancements
          </p>
          <a
            href="https://chromewebstore.google.com/detail/chatgpt-power-up/ooleaojggfoigcdkodigbcjnabidihgi"
            className="inline-block"
          >
            <Button className="bg-white text-indigo-600 hover:bg-gray-100 text-lg px-8 py-4 font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
              Install Free – Make ChatGPT Work For You
            </Button>
          </a>
          <p className="text-sm opacity-75 mt-4">Free • No account required • Works instantly</p>
        </motion.div>
      </div>
    </div>
  );
};

export default UIExtensionPage; 